import { DashboardShell } from 'App/dashboards/_components/DashboardShell'
import { WaterMonitoringTable } from 'App/dashboards/_components/WaterMonitoringTable'
import { compareLabelsNullsLast } from 'App/dashboards/_utils/compareLabelsNullsLast'
import { hasWaterMonitoringAssets } from 'App/dashboards/_utils/hasWaterMonitoringAssets'
import { useWaterDashboardPreferences } from 'App/dashboards/_utils/useWaterDashboardPreferences'
import { useWaterMonitoring } from 'App/dashboards/_utils/useWaterMonitoring'
import { useZoneCrops } from 'App/dashboards/_utils/useZoneCrops'
import { getZonesCropName } from 'App/dashboards/_utils/zoneCrops'
import { translate } from 'i18n/i18n'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { checkAuthorization } from 'utils/checkAuthorization'

const WATER_MONITORING_DASHBOARD_ID = 'water-monitoring'

export const WaterMonitoring = () => {
  // Reuse the water multi-property selection/persistence. Water Monitoring is a fixed past-7-day view,
  // so the persisted date range is unused here (no date-range picker rendered).
  const { options, selectedPropertyIds, onPropertyChange } = useWaterDashboardPreferences({
    dashboardId: WATER_MONITORING_DASHBOARD_ID,
    hasAssets: hasWaterMonitoringAssets,
  })

  const propertyIds = useMemo(() => selectedPropertyIds.map(Number), [selectedPropertyIds])
  const [refreshToken, setRefreshToken] = useState(0)
  const onRefresh = useCallback(() => setRefreshToken((t) => t + 1), [])
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const { sections, loading, planningLoading, error, lastFetchedAt } = useWaterMonitoring({
    propertyIds,
    refreshToken,
  })

  const zoneCrops = useZoneCrops(propertyIds)

  useEffect(() => {
    if (lastFetchedAt) setLastUpdated((prev) => (!prev || lastFetchedAt > prev ? lastFetchedAt : prev))
  }, [lastFetchedAt])

  // Soil-moisture access is per-property and gates the soil columns only. The dashboard is listed for
  // irrigation access too (see dashboards.ts), and those users own the zone data — Applied/Planned,
  // Rain, ETc — so a missing soil permission must not blank the section. `permissions` in deps for
  // reactivity.
  const permissions = userDetailsStore.useSelector((s) => s.permissions)

  const sectionsWithAccess = useMemo(
    () =>
      sections.map((section) => {
        const showCrops = zoneCrops.multiCropPropertyIds.has(section.propertyId)

        const zones = section.zones.map((zone) => ({
          ...zone,
          cropName: getZonesCropName([zone.zoneId], zoneCrops.cropIdsByZoneId),
        }))

        return {
          ...section,
          hasSoilAccess: checkAuthorization({
            permission: 'VIEW_SOIL_MOISTURE_DETAILS',
            entity: section.propertyId,
          }),
          showCrops,
          zones: showCrops ? zones.sort((a, b) => compareLabelsNullsLast(a.cropName, b.cropName)) : zones,
        }
      }),
    [sections, permissions, zoneCrops],
  )

  return (
    <DashboardShell
      title={translate.phrases.banyanApp('Water Monitoring')}
      lastUpdated={lastUpdated}
      onRefresh={onRefresh}
      propertyOptions={options}
      selectedPropertyIds={selectedPropertyIds}
      onPropertyChange={onPropertyChange}
      propertyPickerMode="multi"
      // The table owns its scroll region, so the shell is viewport-height and the page doesn't scroll.
      compactHeaderOnScroll
      widgetOwnsScroll
    >
      <WaterMonitoringTable
        sections={sectionsWithAccess}
        loading={loading}
        planningLoading={planningLoading}
        error={error}
      />
    </DashboardShell>
  )
}
